import { useState } from "react";
import { DashboardLayout } from "@/components/Dashboard/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Clock,
  Plus,
  MoreVertical,
  Edit,
  Trash2,
  Coffee,
  Users,
  Calendar,
  Eye,
  UserPlus,
  X,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import {
  useShiftsSummary,
  useBreakTypes,
  useCurrentShiftAssignments,
  useWorkerShiftAssignments,
  useDeleteShift,
  useDeleteBreakType,
  useShiftBreaks,
  useDeleteShiftBreak,
  useDeleteWorkerShiftAssignment,
  formatTimeRange,
  calculateBreakTime,
} from "@/hooks/useShifts";
import { Shift, BreakType, ShiftSummary, CurrentShiftAssignment, WorkerShiftAssignment } from "@/types/shifts";
import { ShiftFormDialog } from "@/components/Shifts/ShiftFormDialog";
import { BreakTypeFormDialog } from "@/components/Shifts/BreakTypeFormDialog";
import { WorkerAssignmentDialog } from "@/components/Shifts/WorkerAssignmentDialog";
import { ShiftBreakFormDialog } from "@/components/Shifts/ShiftBreakFormDialog";

export default function ShiftManagement() {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("shifts");

  // Dialogs state
  const [shiftDialog, setShiftDialog] = useState<{ open: boolean; shift?: Shift | null }>({
    open: false,
    shift: null,
  });
  const [breakTypeDialog, setBreakTypeDialog] = useState<{ open: boolean; breakType?: BreakType | null }>({
    open: false,
    breakType: null,
  });
  const [assignmentDialog, setAssignmentDialog] = useState<{ open: boolean; workerId?: string; shiftId?: string }>({
    open: false,
  });
  const [shiftBreakDialog, setShiftBreakDialog] = useState<{ open: boolean; shift?: Shift | null }>({
    open: false,
    shift: null,
  });
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    type: "shift" | "breakType" | "assignment" | "shiftBreak";
    id: string;
    shiftId?: string;
    name: string;
  } | null>(null);
  const [detailShift, setDetailShift] = useState<Shift | null>(null);

  // Queries
  const { data: shifts, isLoading: shiftsLoading } = useShiftsSummary();
  const { data: breakTypes, isLoading: breakTypesLoading } = useBreakTypes();
  const { data: currentAssignments, isLoading: assignmentsLoading } = useCurrentShiftAssignments();
  const { data: shiftBreaks } = useShiftBreaks(detailShift?.id);

  // Mutations
  const deleteShiftMutation = useDeleteShift();
  const deleteBreakTypeMutation = useDeleteBreakType();
  const deleteShiftBreakMutation = useDeleteShiftBreak();
  const deleteAssignmentMutation = useDeleteWorkerShiftAssignment();

  const handleDelete = async () => {
    if (!deleteDialog) return;

    try {
      switch (deleteDialog.type) {
        case "shift":
          await deleteShiftMutation.mutateAsync(deleteDialog.id);
          toast({ title: "Turno eliminado" });
          break;
        case "breakType":
          await deleteBreakTypeMutation.mutateAsync(deleteDialog.id);
          toast({ title: "Tipo de descanso eliminado" });
          break;
        case "shiftBreak":
          await deleteShiftBreakMutation.mutateAsync({
            id: deleteDialog.id,
            shiftId: deleteDialog.shiftId!,
          });
          toast({ title: "Descanso eliminado del turno" });
          break;
        case "assignment":
          await deleteAssignmentMutation.mutateAsync(deleteDialog.id);
          toast({ title: "Asignación eliminada" });
          break;
      }
      setDeleteDialog(null);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  // Group assignments by shift
  const assignmentsByShift = currentAssignments?.reduce((acc, assignment) => {
    const shiftId = assignment.shift_id || "unassigned";
    if (!acc[shiftId]) {
      acc[shiftId] = [];
    }
    acc[shiftId].push(assignment);
    return acc;
  }, {} as Record<string, CurrentShiftAssignment[]>);

  return (
    <DashboardLayout>
      <div className="container mx-auto py-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <Clock className="w-8 h-8" />
              Gestión de Turnos
            </h1>
            <p className="text-muted-foreground">
              Configura turnos, descansos y asignaciones de personal
            </p>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Turnos Activos</p>
                  <p className="text-2xl font-bold">
                    {shifts?.filter((s) => s.is_active).length || 0}
                  </p>
                </div>
                <Clock className="w-8 h-8 text-primary" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Tipos de Descanso</p>
                  <p className="text-2xl font-bold">
                    {breakTypes?.filter((b) => b.is_active).length || 0}
                  </p>
                </div>
                <Coffee className="w-8 h-8 text-orange-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Trabajadores Asignados</p>
                  <p className="text-2xl font-bold">
                    {currentAssignments?.filter((a) => a.shift_id).length || 0}
                  </p>
                </div>
                <Users className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Sin Turno</p>
                  <p className="text-2xl font-bold">
                    {currentAssignments?.filter((a) => !a.shift_id).length || 0}
                  </p>
                </div>
                <Calendar className="w-8 h-8 text-red-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3 max-w-lg">
            <TabsTrigger value="shifts">Turnos</TabsTrigger>
            <TabsTrigger value="breaks">Descansos</TabsTrigger>
            <TabsTrigger value="assignments">Asignaciones</TabsTrigger>
          </TabsList>

          {/* Shifts Tab */}
          <TabsContent value="shifts" className="space-y-4">
            <div className="flex justify-end">
              <Button onClick={() => setShiftDialog({ open: true, shift: null })}>
                <Plus className="w-4 h-4 mr-2" />
                Nuevo Turno
              </Button>
            </div>

            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Color</TableHead>
                    <TableHead>Nombre</TableHead>
                    <TableHead>Código</TableHead>
                    <TableHead>Horario</TableHead>
                    <TableHead>Descansos</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {shiftsLoading ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8">
                        Cargando turnos...
                      </TableCell>
                    </TableRow>
                  ) : shifts?.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8">
                        No hay turnos configurados
                      </TableCell>
                    </TableRow>
                  ) : (
                    shifts?.map((shift) => (
                      <TableRow key={shift.id}>
                        <TableCell>
                          <div
                            className="w-6 h-6 rounded-full"
                            style={{ backgroundColor: shift.color }}
                          />
                        </TableCell>
                        <TableCell className="font-medium">{shift.name}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{shift.code}</Badge>
                        </TableCell>
                        <TableCell>
                          {formatTimeRange(shift.start_time, shift.end_time, shift.crosses_midnight)}
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary">
                            {shift.break_count} ({shift.total_break_minutes} min)
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant={shift.is_active ? "default" : "secondary"}>
                            {shift.is_active ? "Activo" : "Inactivo"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => setDetailShift(shift)}>
                                <Eye className="w-4 h-4 mr-2" />
                                Ver descansos
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => setShiftBreakDialog({ open: true, shift })}
                              >
                                <Coffee className="w-4 h-4 mr-2" />
                                Agregar descanso
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => setShiftDialog({ open: true, shift })}
                              >
                                <Edit className="w-4 h-4 mr-2" />
                                Editar
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                className="text-destructive"
                                onClick={() =>
                                  setDeleteDialog({
                                    open: true,
                                    type: "shift",
                                    id: shift.id,
                                    name: shift.name,
                                  })
                                }
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Eliminar
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Card>

            {/* Shift Detail Panel */}
            {detailShift && (
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <div
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: detailShift.color }}
                    />
                    Descansos de {detailShift.name}
                  </CardTitle>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => setShiftBreakDialog({ open: true, shift: detailShift })}
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Agregar
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setDetailShift(null)}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {shiftBreaks?.length === 0 ? (
                    <p className="text-muted-foreground text-center py-4">
                      No hay descansos configurados para este turno
                    </p>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Tipo</TableHead>
                          <TableHead>Hora</TableHead>
                          <TableHead>Duración</TableHead>
                          <TableHead>Obligatorio</TableHead>
                          <TableHead className="text-right">Acciones</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {shiftBreaks?.map((sb) => (
                          <TableRow key={sb.id}>
                            <TableCell>{sb.break_type?.name}</TableCell>
                            <TableCell>
                              {calculateBreakTime(detailShift.start_time, sb.start_offset_minutes)}
                            </TableCell>
                            <TableCell>{sb.duration_minutes} min</TableCell>
                            <TableCell>
                              <Badge variant={sb.is_mandatory ? "default" : "secondary"}>
                                {sb.is_mandatory ? "Sí" : "No"}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() =>
                                  setDeleteDialog({
                                    open: true,
                                    type: "shiftBreak",
                                    id: sb.id,
                                    shiftId: detailShift.id,
                                    name: sb.break_type?.name || "descanso",
                                  })
                                }
                              >
                                <Trash2 className="w-4 h-4 text-destructive" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Break Types Tab */}
          <TabsContent value="breaks" className="space-y-4">
            <div className="flex justify-end">
              <Button onClick={() => setBreakTypeDialog({ open: true, breakType: null })}>
                <Plus className="w-4 h-4 mr-2" />
                Nuevo Tipo de Descanso
              </Button>
            </div>

            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nombre</TableHead>
                    <TableHead>Código</TableHead>
                    <TableHead>Duración</TableHead>
                    <TableHead>Pagado</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {breakTypesLoading ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8">
                        Cargando tipos de descanso...
                      </TableCell>
                    </TableRow>
                  ) : breakTypes?.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8">
                        No hay tipos de descanso configurados
                      </TableCell>
                    </TableRow>
                  ) : (
                    breakTypes?.map((bt) => (
                      <TableRow key={bt.id}>
                        <TableCell className="font-medium">{bt.name}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{bt.code}</Badge>
                        </TableCell>
                        <TableCell>{bt.default_duration_minutes} min</TableCell>
                        <TableCell>
                          <Badge variant={bt.is_paid ? "default" : "secondary"}>
                            {bt.is_paid ? "Sí" : "No"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant={bt.is_active ? "default" : "secondary"}>
                            {bt.is_active ? "Activo" : "Inactivo"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={() => setBreakTypeDialog({ open: true, breakType: bt })}
                              >
                                <Edit className="w-4 h-4 mr-2" />
                                Editar
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                className="text-destructive"
                                onClick={() =>
                                  setDeleteDialog({
                                    open: true,
                                    type: "breakType",
                                    id: bt.id,
                                    name: bt.name,
                                  })
                                }
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Eliminar
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>

          {/* Assignments Tab */}
          <TabsContent value="assignments" className="space-y-4">
            <div className="flex justify-end">
              <Button onClick={() => setAssignmentDialog({ open: true })}>
                <UserPlus className="w-4 h-4 mr-2" />
                Asignar Trabajador
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {shifts?.filter((s) => s.is_active).map((shift) => (
                <Card key={shift.id}>
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <div
                        className="w-4 h-4 rounded-full"
                        style={{ backgroundColor: shift.color }}
                      />
                      {shift.name}
                      <Badge variant="outline" className="ml-auto">
                        {assignmentsByShift?.[shift.id]?.length || 0}
                      </Badge>
                    </CardTitle>
                    <p className="text-sm text-muted-foreground">
                      {formatTimeRange(shift.start_time, shift.end_time, shift.crosses_midnight)}
                    </p>
                  </CardHeader>
                  <CardContent>
                    {!assignmentsByShift?.[shift.id]?.length ? (
                      <p className="text-muted-foreground text-sm text-center py-4">
                        Sin trabajadores asignados
                      </p>
                    ) : (
                      <div className="space-y-2">
                        {assignmentsByShift[shift.id].slice(0, 5).map((assignment) => (
                          <div
                            key={assignment.worker_id}
                            className="flex items-center justify-between text-sm"
                          >
                            <span>{assignment.full_name}</span>
                            {assignment.area && (
                              <Badge variant="secondary" className="text-xs">
                                {assignment.area}
                              </Badge>
                            )}
                          </div>
                        ))}
                        {assignmentsByShift[shift.id].length > 5 && (
                          <p className="text-xs text-muted-foreground text-center">
                            +{assignmentsByShift[shift.id].length - 5} más
                          </p>
                        )}
                      </div>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full mt-4"
                      onClick={() =>
                        setAssignmentDialog({ open: true, shiftId: shift.id })
                      }
                    >
                      <UserPlus className="w-4 h-4 mr-1" />
                      Asignar
                    </Button>
                  </CardContent>
                </Card>
              ))}

              {/* Unassigned Workers */}
              {assignmentsByShift?.["unassigned"]?.length > 0 && (
                <Card className="border-dashed">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-lg text-muted-foreground">
                      <Calendar className="w-4 h-4" />
                      Sin Turno Asignado
                      <Badge variant="destructive" className="ml-auto">
                        {assignmentsByShift["unassigned"].length}
                      </Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {assignmentsByShift["unassigned"].slice(0, 5).map((assignment) => (
                        <div
                          key={assignment.worker_id}
                          className="flex items-center justify-between text-sm"
                        >
                          <span>{assignment.full_name}</span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              setAssignmentDialog({
                                open: true,
                                workerId: assignment.worker_id,
                              })
                            }
                          >
                            <UserPlus className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                      {assignmentsByShift["unassigned"].length > 5 && (
                        <p className="text-xs text-muted-foreground text-center">
                          +{assignmentsByShift["unassigned"].length - 5} más
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Dialogs */}
      <ShiftFormDialog
        open={shiftDialog.open}
        onOpenChange={(open) => setShiftDialog({ open, shift: null })}
        shift={shiftDialog.shift}
      />

      <BreakTypeFormDialog
        open={breakTypeDialog.open}
        onOpenChange={(open) => setBreakTypeDialog({ open, breakType: null })}
        breakType={breakTypeDialog.breakType}
      />

      <WorkerAssignmentDialog
        open={assignmentDialog.open}
        onOpenChange={(open) => setAssignmentDialog({ open })}
        preselectedWorkerId={assignmentDialog.workerId}
        preselectedShiftId={assignmentDialog.shiftId}
      />

      {shiftBreakDialog.shift && (
        <ShiftBreakFormDialog
          open={shiftBreakDialog.open}
          onOpenChange={(open) => setShiftBreakDialog({ open, shift: null })}
          shift={shiftBreakDialog.shift}
        />
      )}

      <AlertDialog open={!!deleteDialog} onOpenChange={() => setDeleteDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Estás seguro?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Se eliminará permanentemente{" "}
              <strong>{deleteDialog?.name}</strong>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}
